/**
 * SkyNexa Technologies - Client Engine
 * Multi-Theme (Dark, Light, System) Manager & Modular Page Controller
 */

// Immediate Theme Initialization to avoid flash
(() => {
  const savedTheme = localStorage.getItem('skynexa-theme') || 'system';
  if (savedTheme === 'system') {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  } else {
    document.documentElement.setAttribute('data-theme', savedTheme);
  }
})();

document.addEventListener('DOMContentLoaded', () => {
  initThemeSwitch();
  initMobileNav();
  highlightActiveNav();
  initCaptionRotator();
  initCommandButtons();
  initCurrentYear();

  // Page-specific: only the stack page carries the interactive terminal.
  if (document.getElementById('terminal-cli-input')) initInteractiveTerminal();
});

/* ==========================================================================
   1. Multi-Theme (Light, Dark, System) Manager
   ========================================================================== */
function initThemeSwitch() {
  const switchBtns = document.querySelectorAll('.theme-switch-btn');
  const savedTheme = localStorage.getItem('skynexa-theme') || 'system';

  applyTheme(savedTheme);

  switchBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const selectedTheme = btn.getAttribute('data-theme-value');
      localStorage.setItem('skynexa-theme', selectedTheme);
      applyTheme(selectedTheme);
    });
  });

  // Listen to OS system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    const currentTheme = localStorage.getItem('skynexa-theme') || 'system';
    if (currentTheme === 'system') {
      document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
    }
  });
}

function applyTheme(theme) {
  const switchBtns = document.querySelectorAll('.theme-switch-btn');

  // Reflect state for both sighted users (class) and assistive tech
  // (aria-pressed). The class alone leaves screen-reader users unable to tell
  // which theme is selected.
  switchBtns.forEach((btn) => {
    const isActive = btn.getAttribute('data-theme-value') === theme;
    btn.classList.toggle('active', isActive);
    btn.setAttribute('aria-pressed', String(isActive));
  });

  if (theme === 'system') {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  } else {
    document.documentElement.setAttribute('data-theme', theme);
  }
}

/* ==========================================================================
   2. Active Page Navigation Sync
   ========================================================================== */
function highlightActiveNav() {
  // The nav partial already marks the active link at build time, which is what
  // crawlers and no-JS visitors see. This only corrects the case where the page
  // is served from a directory path ("/" rather than "/index.html").
  const file = window.location.pathname.split('/').pop() || 'index.html';

  document.querySelectorAll('.nav-link').forEach((link) => {
    const href = (link.getAttribute('href') || '').split('/').pop();
    const isActive = href === file;
    link.classList.toggle('active', isActive);
    if (isActive) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  });
}

function initInteractiveTerminal() {
  const input = document.getElementById('terminal-cli-input');
  if (!input) return;

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      const command = input.value.trim();
      if (command) {
        runTerminalCommand(command);
        input.value = '';
      }
    }
  });
}

/**
 * The commands the terminal understands, and what each one prints.
 *
 * Output is class-based rather than inline-styled. The previous version wrote
 * a colour into every element it created, which meant none of it followed the
 * theme, several of the values were invisible against the light theme's
 * terminal, and the whole widget would have been blanked by a strict
 * style-src CSP. Colours now come from the --on-dark-* tokens in
 * css/stack.css, which are chosen for this pane specifically.
 */
const TERMINAL_COMMANDS = {
  help: () => `
    <div class="term-head">Available commands:</div>
    ${[
      ['services', 'List what we build'],
      ['web-stack', 'Inspect the web stack we use'],
      ['lighthouse', 'Run a simulated Core Web Vitals audit'],
      ['terraform plan', 'Run a simulated infrastructure plan'],
      ['clear', 'Clear the output above'],
      ['hire', 'Go to the enquiry form'],
    ]
      .map(
        ([name, description]) =>
          `<div class="term-row"><span class="term-key">${name}</span><span class="term-muted">${description}</span></div>`,
      )
      .join('')}
  `,

  services: () => `
    <div class="term-head term-head--emerald">What we build</div>
    <div>&bull; <strong>01_fullstack_web</strong> : Next.js, React, TypeScript, PostgreSQL, SaaS</div>
    <div>&bull; <strong>02_headless_commerce</strong> : Shopify Plus, Medusa, edge caching</div>
    <div>&bull; <strong>03_k8s_architecture</strong> : Multi-AZ EKS/GKE, Karpenter, Cilium eBPF</div>
    <div>&bull; <strong>04_gitops_automation</strong> : ArgoCD, GitHub Actions, canary rollouts</div>
    <div>&bull; <strong>05_devsecops_iac</strong> : Modular Terraform, OpenTofu, Vault</div>
    <div>&bull; <strong>06_finops_performance</strong> : Cloud cost reduction and Core Web Vitals</div>
  `,

  'web-stack': () => `
    <div class="term-head">The web stack we work in</div>
    <div>&bull; <strong>Frontend</strong> : Next.js App Router, React, TypeScript, Tailwind CSS</div>
    <div>&bull; <strong>Backend</strong> : Node.js, Fastify, Python FastAPI, Go, GraphQL, tRPC</div>
    <div>&bull; <strong>Database</strong> : PostgreSQL, Supabase, Redis, Prisma / Drizzle</div>
    <div>&bull; <strong>Testing</strong> : Vitest, Playwright, Cypress, Storybook</div>
  `,

  lighthouse: () => `
    <div class="term-head term-head--emerald">Simulated Lighthouse audit</div>
    <div class="term-scores">
      ${['Performance', 'Accessibility', 'Best Practices', 'SEO']
        .map(
          (label) =>
            `<div class="term-score"><div class="term-score__value">100</div><div class="term-score__label">${label}</div></div>`,
        )
        .join('')}
    </div>
    <div class="term-muted">A demonstration of the shape of the report, not a live measurement.</div>
  `,

  terraform: () => TERMINAL_COMMANDS['terraform plan'](),

  'terraform plan': () => `
    <div>Running Terraform v1.8.4 on AWS us-east-1...</div>
    <div class="term-muted">module.eks.aws_eks_cluster.primary: Refreshing state...</div>
    <div class="term-rule">------------------------------------------------------------------------</div>
    <div class="term-head term-head--emerald">Plan: 4 to add, 2 to change, 0 to destroy.</div>
  `,
};

/**
 * Run one command and append its output to the terminal pane.
 *
 * @param {string} rawCmd the command as typed or as carried on a data-cmd
 */
function runTerminalCommand(rawCmd) {
  const terminalBody = document.getElementById('interactive-terminal-body');
  if (!terminalBody) return;

  const cmd = rawCmd.trim().toLowerCase();

  const cmdLine = document.createElement('div');
  cmdLine.className = 'term-line';
  cmdLine.innerHTML = `<span class="term-prompt">guest@skynexa:~$</span> <span class="term-cmd">${escapeHtml(rawCmd)}</span>`;
  terminalBody.appendChild(cmdLine);

  if (cmd === 'clear') {
    terminalBody.innerHTML = '';
    return;
  }

  if (cmd === 'hire') {
    window.location.href = '/contact.html';
    return;
  }

  const responseLine = document.createElement('div');
  responseLine.className = 'term-out';

  const render = TERMINAL_COMMANDS[cmd];
  responseLine.innerHTML = render
    ? render()
    : // A real button, not a span with an onclick. The previous version wrote
      // onclick="runTerminalCommand('help')", which stopped working the moment
      // app.js became a module and the function was no longer global — so the
      // one affordance offered to someone who had just mistyped did nothing.
      `<div class="term-error">command not found: ${escapeHtml(rawCmd)}. Try
       <button type="button" class="term-inline-cmd" data-cmd="help">help</button>
       for the list.</div>`;

  terminalBody.appendChild(responseLine);
  terminalBody.scrollTop = terminalBody.scrollHeight;
}

/* ==========================================================================
   8. Toast Notifications
   ========================================================================== */
function initMobileNav() {
  const toggle = document.getElementById('mobile-toggle');
  const navLinks = document.getElementById('nav-links');
  if (!toggle || !navLinks) return;

  const setOpen = (open) => {
    // A class, not a pile of inline styles: the presentation belongs in CSS,
    // and aria-expanded is what tells assistive tech the menu's state.
    navLinks.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', open ? 'Close navigation menu' : 'Open navigation menu');
  };

  setOpen(false);

  toggle.addEventListener('click', () => {
    setOpen(!navLinks.classList.contains('is-open'));
  });

  navLinks.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => setOpen(false));
  });

  // Escape closes the menu and returns focus to the control that opened it —
  // otherwise keyboard focus is stranded inside a dismissed menu.
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && navLinks.classList.contains('is-open')) {
      setOpen(false);
      toggle.focus();
    }
  });

  // Leaving the mobile breakpoint must not strand the menu in its open state.
  window.matchMedia('(min-width: 769px)').addEventListener('change', (event) => {
    if (event.matches) setOpen(false);
  });
}

/* ==========================================================================
   9b. Terminal quick-command buttons
   Delegated from data-cmd attributes rather than inline onclick handlers, so
   the site can ship a Content-Security-Policy without 'unsafe-inline'.
   ========================================================================== */
function initCommandButtons() {
  // Delegated from the document rather than bound per button, so the "help"
  // button the terminal itself prints after an unrecognised command works too.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-cmd]');
    if (!trigger) return;
    runTerminalCommand(trigger.dataset.cmd);
  });
}

/* ==========================================================================
   9c. Footer copyright year
   ========================================================================== */
function initCurrentYear() {
  const year = String(new Date().getFullYear());
  document.querySelectorAll('[data-current-year]').forEach((node) => {
    node.textContent = year;
  });
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/* ==========================================================================
   10. Interactive Caption Rotator Engine
   ========================================================================== */
const INTERACTIVE_CAPTIONS = [
  '"Websites that work for your business." ⭐',
  '"Just clear, simple advice."',
  '"One affordable price, agreed before we start."',
  '"You own everything we build."',
  '"Any tech problem — we\'ll help, or teach you."',
];

let captionIndex = 0;
let captionTimer = null;

function initCaptionRotator() {
  const rotatorText = document.getElementById('rotator-text');
  const prevBtn = document.getElementById('caption-prev');
  const nextBtn = document.getElementById('caption-next');
  const rotatorContainer = document.getElementById('interactive-caption-rotator');

  if (!rotatorText) return;

  function setCaption(index, direction = 'next') {
    captionIndex = (index + INTERACTIVE_CAPTIONS.length) % INTERACTIVE_CAPTIONS.length;
    rotatorText.style.opacity = '0';
    rotatorText.style.transform = direction === 'next' ? 'translateY(6px)' : 'translateY(-6px)';

    setTimeout(() => {
      rotatorText.textContent = INTERACTIVE_CAPTIONS[captionIndex];
      rotatorText.style.opacity = '1';
      rotatorText.style.transform = 'translateY(0)';
    }, 180);
  }

  function startAutoCycle() {
    stopAutoCycle();
    captionTimer = setInterval(() => {
      setCaption(captionIndex + 1, 'next');
    }, 4000);
  }

  function stopAutoCycle() {
    if (captionTimer) clearInterval(captionTimer);
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      setCaption(captionIndex - 1, 'prev');
      startAutoCycle();
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      setCaption(captionIndex + 1, 'next');
      startAutoCycle();
    });
  }

  if (rotatorContainer) {
    rotatorContainer.addEventListener('click', () => {
      setCaption(captionIndex + 1, 'next');
      startAutoCycle();
    });
    rotatorContainer.addEventListener('mouseenter', stopAutoCycle);
    rotatorContainer.addEventListener('mouseleave', startAutoCycle);
  }

  startAutoCycle();
}

/* This file is an entry module: it is loaded by a <script type="module"> tag
   and imported by nothing, so it exports nothing. Being a module is what keeps
   these names off window — the inline handlers that used to need them were
   replaced by delegated listeners, see initCommandButtons. */
